import { Box, Typography, Skeleton, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { CampaignPerformance } from '../services/responses/dashboard.response';

interface Props {
    data?: CampaignPerformance[];
}

const real = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const CampaignTable = ({ data }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const rows = data ?? [];

    const cell = {
        padding: '8px 10px',
        fontSize: '0.8rem',
        borderBottom: `1px solid ${cc.border}`,
        whiteSpace: 'nowrap' as const,
    };

    return (
        <Box
            sx={{
                background: cc.cardBg,
                border: `1px solid ${cc.border}`,
                borderRadius: 3,
                p: 2,
            }}
        >
            <Typography variant="body2" fontWeight={700} sx={{ color: cc.textPrimary, mb: 0.3 }}>
                {t('crmDashboard.campanhas')}
            </Typography>
            <Typography variant="caption" sx={{ color: cc.textSecondary, display: 'block', mb: 1.5 }}>
                {t('crmDashboard.campanhasAjuda')}
            </Typography>

            {!data ? (
                <Skeleton variant="rounded" height={140} />
            ) : rows.length === 0 ? (
                <Typography
                    variant="caption"
                    sx={{ color: cc.textMuted, py: 5, display: 'block', textAlign: 'center' }}
                >
                    {t('crmDashboard.semCampanha')}
                </Typography>
            ) : (
                // Tabela larga rola dentro do próprio bloco; a página nunca rola de lado.
                <Box sx={{ overflowX: 'auto' }}>
                    <Box
                        component="table"
                        sx={{
                            width: '100%',
                            minWidth: 1020,
                            borderCollapse: 'collapse',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        <thead>
                            <tr>
                                {[
                                    ['campanha', 'left'],
                                    ['impressoes', 'right'],
                                    ['cliques', 'right'],
                                    ['gasto', 'right'],
                                    ['leads', 'right'],
                                    ['custoPorLead', 'right'],
                                    ['ganhos', 'right'],
                                    ['conversao', 'right'],
                                    ['custoPorGanho', 'right'],
                                    ['receitaColuna', 'right'],
                                    ['retorno', 'right'],
                                ].map(([key, align]) => (
                                    <Box
                                        key={key}
                                        component="th"
                                        sx={{
                                            ...cell,
                                            textAlign: align as 'left' | 'right',
                                            color: cc.textMuted,
                                            fontSize: '10px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '.08em',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {t(`crmDashboard.${key}`)}
                                    </Box>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.adId}>
                                    <Box
                                        component="td"
                                        sx={{
                                            ...cell,
                                            color: cc.textPrimary,
                                            maxWidth: 260,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        <Tooltip title={`ID ${row.adId}`}>
                                            <span>{row.name}</span>
                                        </Tooltip>
                                    </Box>
                                    <Box component="td" sx={{ ...cell, textAlign: 'right', color: cc.textSecondary }}>
                                        {row.impressions.toLocaleString('pt-BR')}
                                    </Box>
                                    <Box component="td" sx={{ ...cell, textAlign: 'right', color: cc.textSecondary }}>
                                        {row.clicks.toLocaleString('pt-BR')}
                                    </Box>
                                    <Box component="td" sx={{ ...cell, textAlign: 'right', color: cc.textSecondary }}>
                                        {real(row.spend)}
                                    </Box>
                                    <Box component="td" sx={{ ...cell, textAlign: 'right', color: cc.textPrimary, fontWeight: 700 }}>
                                        {row.leads}
                                    </Box>
                                    <Box
                                        component="td"
                                        sx={{
                                            ...cell,
                                            textAlign: 'right',
                                            color: row.costPerLead ? cc.eventWarning : cc.textMuted,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {/* Sem lead não há custo por lead: dividir por zero viraria "infinito". */}
                                        {row.costPerLead ? real(row.costPerLead) : '—'}
                                    </Box>
                                    <Box
                                        component="td"
                                        sx={{
                                            ...cell,
                                            textAlign: 'right',
                                            color: row.won > 0 ? cc.eventSuccess : cc.textMuted,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {row.won}
                                    </Box>
                                    <Box
                                        component="td"
                                        sx={{
                                            ...cell,
                                            textAlign: 'right',
                                            color: cc.textSecondary,
                                        }}
                                    >
                                        {/* Anúncio sem lead não tem conversão: zero por cento diria que não fechou. */}
                                        {row.conversionRate === null
                                            || row.conversionRate === undefined
                                            ? '—'
                                            : `${row.conversionRate.toLocaleString('pt-BR')}%`}
                                    </Box>
                                    <Box
                                        component="td"
                                        sx={{
                                            ...cell,
                                            textAlign: 'right',
                                            color: row.costPerWon ? cc.textPrimary : cc.textMuted,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {row.costPerWon ? real(row.costPerWon) : '—'}
                                    </Box>
                                    <Box
                                        component="td"
                                        sx={{
                                            ...cell,
                                            textAlign: 'right',
                                            color: row.wonValue > 0 ? cc.textPrimary : cc.textMuted,
                                        }}
                                    >
                                        {row.wonValue > 0 ? real(row.wonValue) : '—'}
                                    </Box>
                                    <Box
                                        component="td"
                                        sx={{
                                            ...cell,
                                            textAlign: 'right',
                                            fontWeight: 700,
                                            color: row.roas == null
                                                ? cc.textMuted
                                                : row.roas >= 1
                                                    ? cc.eventSuccess
                                                    : cc.eventDanger,
                                        }}
                                    >
                                        {/* Sem valor lançado não dá para dizer se voltou: vazio é honesto. */}
                                        {row.roas == null
                                            ? '—'
                                            : `${row.roas.toLocaleString('pt-BR')}x`}
                                    </Box>
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
